import { BCNotification, findMessage } from '@bimser/components';
import { RxEventEmitter, createGuid } from '@bimser/core';
import { createElement } from 'react';
import { call } from 'redux-saga/effects';
import {
	DeployAgentEntities,
	HTTPRequest,
	HTTPRequestConfig,
	MessageBox,
	MessageBoxButtons,
	Mobile
} from '..';
import ProjectDeploymentProgressContainer from '../components/ProjectDeploymentProgress/containers';
import {
	AgentProjects,
	BaseServicePayload,
	DeployStatusResponse,
	IGlobalProjectInterface,
	RunFlowServicePayload,
	RunFormServicePayload
} from '../entities/ProjectHelper';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _unset from 'lodash/unset';

function getDeployAgentUrlTemplate(
	deployAgent: any,
	projectName: string,
	projectVersion: number,
	restOfAll: string
) {
	if (!deployAgent.url.endsWith('/')) {
		deployAgent.url += '/';
	}
	return `${deployAgent.url}apps/${projectName}/${projectVersion ?? 'latest'}/api${
		restOfAll ? '/' + restOfAll : ''
	}`;
}

function* _RunFlowService(payload: RunFlowServicePayload) {
	const deployAgent: any = ProjectHelper.getDeployAgent(
		payload.projectName,
		payload.projectVersion
	);
	if (deployAgent) {
		try {
			const requestUrl = getDeployAgentUrlTemplate(
				deployAgent,
				payload.projectName,
				payload.projectVersion,
				`${payload.flowName}/${payload.serviceName}`
			);
			const serviceResponse = yield call(
				sendServiceRequestGenerator,
				payload.serviceMethod,
				requestUrl,
				payload.requestParameters,
				payload.requestConfig
			);
			return yield call(OnServiceResponseGenerator, requestUrl, serviceResponse, payload);
		} catch (error) {
			console.log(
				`[${payload.projectName}][${payload.flowName}][${payload.serviceName}] Error.`,
				error
			);
			throw error;
		}
	} else {
		onDeployAgentNotFound();
		throw new Error('Deploy agent not found');
	}
}

function* _RunFormService(payload: RunFormServicePayload) {
	const deployAgent: any = ProjectHelper.getDeployAgent(
		payload.projectName,
		payload.projectVersion
	);
	if (deployAgent) {
		try {
			const requestUrl = getDeployAgentUrlTemplate(
				deployAgent,
				payload.projectName,
				payload.projectVersion,
				`${payload.formName}/${payload.serviceName}`
			);
			const serviceResponse = yield call(
				sendServiceRequestGenerator,
				payload.serviceMethod,
				requestUrl,
				payload.requestParameters,
				payload.requestConfig
			);
			return yield call(OnServiceResponseGenerator, requestUrl, serviceResponse, payload);
		} catch (error) {
			console.log(
				`[${payload.projectName}][${payload.formName}][${payload.serviceName}] Error.`,
				error
			);
			throw error;
		}
	} else {
		onDeployAgentNotFound();
		throw new Error('Deploy agent not found');
	}
}

function* OnServiceResponseGenerator(
	requestUrl: string,
	serviceResponse: any,
	payload: BaseServicePayload
): Generator<any, any, any> {
	if (!serviceResponse) {
		console.log(`[${payload.projectName}][${payload.serviceName}] Response Error.`);
		return null;
	}
	if (Object.keys(serviceResponse.data).includes('queued')) {
		//Project is not ready. listen socket
		const modalId = openDeploymentProgressModal(
			payload.projectName,
			payload.projectVersion,
			true
		);
		const deployStatus: DeployStatusResponse = yield waitForDeployStatus(
			payload.projectName,
			payload.projectVersion,
			payload.cancelOnError
		);
		if (deployStatus == DeployStatusResponse.Success) {
			const _serviceResponse = yield call(
				sendServiceRequestGenerator,
				payload.serviceMethod,
				requestUrl,
				payload.requestParameters,
				payload.requestConfig
			);
			return yield call(OnServiceResponseGenerator, requestUrl, _serviceResponse, payload);
		} else if (
			(deployStatus == DeployStatusResponse.Error && payload.cancelOnError) ||
			deployStatus == DeployStatusResponse.Cancelled
		) {
			MessageBox.closeModal(modalId);
			throw new Error(deployStatus == DeployStatusResponse.Error ? null : '-1');
		}
	} else {
		return serviceResponse;
	}
}

function _RunFormServicePromise<T = any>(payload: RunFormServicePayload) {
	const deployAgent: any = ProjectHelper.getDeployAgent(
		payload.projectName,
		payload.projectVersion
	);
	return new Promise<T>((resolve, reject) => {
		if (deployAgent) {
			try {
				const requestUrl = getDeployAgentUrlTemplate(
					deployAgent,
					payload.projectName,
					payload.projectVersion,
					`${payload.formName}/${payload.serviceName}`
				);
				sendServiceRequestPromise(
					payload.serviceMethod,
					requestUrl,
					getServiceResponse(resolve, reject, payload, requestUrl),
					getServiceError(reject, payload),
					payload.requestParameters,
					payload.requestConfig
				);
			} catch (error) {
				console.log(
					`[${payload.projectName}][${payload.formName}][${payload.serviceName}] Error.`,
					error
				);
				reject(error);
			}
		} else {
			onDeployAgentNotFound();
			reject('Deploy agent not found');
		}
	});
}

function _RunFlowServicePromise(payload: RunFlowServicePayload) {
	const deployAgent: any = ProjectHelper.getDeployAgent(
		payload.projectName,
		payload.projectVersion
	);
	return new Promise<any>((resolve, reject) => {
		if (deployAgent) {
			try {
				const requestUrl = getDeployAgentUrlTemplate(
					deployAgent,
					payload.projectName,
					payload.projectVersion,
					`${payload.flowName}/${payload.serviceName}`
				);
				sendServiceRequestPromise(
					payload.serviceMethod,
					requestUrl,
					getServiceResponse(resolve, reject, payload, requestUrl),
					getServiceError(reject, payload),
					payload.requestParameters,
					payload.requestConfig
				);
			} catch (error) {
				console.log(
					`[${payload.projectName}][${payload.flowName}][${payload.serviceName}] Error.`,
					error
				);
				reject(error);
			}
		} else {
			onDeployAgentNotFound();
			reject('Deploy agent not found');
		}
	});
}

function onDeployAgentNotFound() {
	BCNotification.severeWarning({
		message: findMessage.get('102684'),
		description: findMessage.get('102685')
	});
	sessionStorage.removeItem('deployAgents');
}

function _GetDeploymentUrl(projectName: string, defaultUrl: string, projectVersion?: number) {
	const deployAgent: any = ProjectHelper.getDeployAgent(projectName, projectVersion);
	if (deployAgent) {
		return getDeployAgentUrlTemplate(deployAgent, projectName, projectVersion, null);
	} else {
		return defaultUrl;
	}
}

function waitForDeployStatus(
	projectName: string,
	projectVersion?: number,
	cancelOnError?: boolean
): Promise<DeployStatusResponse> {
	return new Promise(resolve => {
		let _sub: any = null;
		RxEventEmitter.listen(DeployAgentEntities.DeployAgentEventKey, {
			next: (message: DeployAgentEntities.IDeployAgentMessage) => {
				if (
					message?.ProjectName == projectName &&
					(projectVersion ? message?.ProjectVersion == projectVersion : true)
				) {
					if (message.Type == DeployAgentEntities.OutputMessageType.Success) {
						resolve(DeployStatusResponse.Success);
						_sub?.unsubscribe();
					} else if (
						message.Status == DeployAgentEntities.OutputMessageStatus.Cancelled
					) {
						resolve(DeployStatusResponse.Cancelled);
						_sub?.unsubscribe();
					} else if (
						message.Type == DeployAgentEntities.OutputMessageType.Error &&
						cancelOnError
					) {
						resolve(DeployStatusResponse.Error);
						_sub?.unsubscribe();
					}
				}
			}
		}).then(sub => (_sub = sub));
	});
}

const getServiceResponse =
	(resolve: Function, reject: Function, payload: any, requestUrl: string) =>
	(serviceResponse: any) => {
		if (!serviceResponse) {
			console.log(
				`[${payload.projectName}][${payload.flowName || payload.formName}][${
					payload.serviceName
				}] Response Error.`
			);
			reject();
			return;
		}
		if (Object.keys(serviceResponse.data).includes('queued')) {
			//Project is not ready. listen socket
			const modalId = openDeploymentProgressModal(
				payload.projectName,
				payload.projectVersion,
				true
			);
			waitForDeployStatus(
				payload.projectName,
				payload.projectVersion,
				payload.cancelOnError
			).then((res: DeployStatusResponse) => {
				if (res == DeployStatusResponse.Success) {
					sendServiceRequestPromise(
						payload.serviceMethod,
						requestUrl,
						getServiceResponse(resolve, reject, payload, requestUrl),
						getServiceError(reject, payload),
						payload.requestParameters,
						payload.requestConfig
					);
				} else if (
					(res == DeployStatusResponse.Error && payload.cancelOnError) ||
					res == DeployStatusResponse.Cancelled
				) {
					MessageBox.closeModal(modalId);
					reject(res == DeployStatusResponse.Error ? null : -1);
				}
			});
		} else {
			resolve(serviceResponse);
		}
	};

const getServiceError = (reject: Function, payload: any) => (error: any) => {
	if (error?.message !== 'canceled') {
		console.log(
			`[${payload.projectName}][${payload.flowName || payload.formName}][${
				payload.serviceName
			}] Error.`,
			error
		);
	}

	Mobile.MobileView.mobileServiceResponse(false, payload.serviceName, error);

	reject(error);
};

function sendServiceRequestPromise(
	method: 'get' | 'post',
	requestUrl: string,
	onResponse: (response: any) => void,
	onError: (error: any) => void,
	requestParameters?: any,
	requestConfig?: HTTPRequestConfig
) {
	if (method == 'get') {
		HTTPRequest.get(requestUrl, requestConfig).then(onResponse).catch(onError);
	} else {
		HTTPRequest.post(requestUrl, requestParameters, requestConfig)
			.then(onResponse)
			.catch(onError);
	}
}

function* sendServiceRequestGenerator(
	method: 'get' | 'post',
	requestUrl: string,
	requestParameters?: any,
	requestConfig?: HTTPRequestConfig
) {
	if (method == 'get') {
		return yield HTTPRequest.get(requestUrl, requestConfig);
	} else {
		return yield HTTPRequest.post(requestUrl, requestParameters, requestConfig);
	}
}

function openDeploymentProgressModal(
	projectName: string,
	projectVersion?: number,
	autoClose?: boolean
) {
	const modalId = createGuid();
	MessageBox.show({
		id: modalId,
		content: createElement(ProjectDeploymentProgressContainer, {
			modalId,
			projectName,
			projectVersion,
			autoClose
		}),
		props: {
			closable: false,
			maskClosable: false,
			keyboard: false,
			footer: null,
			title: null,
			renderHeader: () => null
		},
		width: '400px',
		buttons: MessageBoxButtons.OK,
		notInCloseIcon: true
	});
	return modalId;
}

function _increaseProjectReference(name: string) {
	const currentCount = _get(_getGlobalProjectsObject(), ['projectReferenceCounts', name]) | 0;
	_set(_getGlobalProjectsObject(), ['projectReferenceCounts', name], currentCount + 1);
}

function _decreaseProjectReference(name: string) {
	const currentCount = _get(_getGlobalProjectsObject(), ['projectReferenceCounts', name]);
	const nextCount = currentCount - 1;
	if (nextCount <= 0) {
		_unset(_getGlobalProjectsObject(), ['projectReferenceCounts', name]);
	} else {
		_set(_getGlobalProjectsObject(), ['projectReferenceCounts', name], currentCount - 1);
	}
}

function _getProjectReferenceCount(name: string) {
	return _get(_getGlobalProjectsObject(), ['projectReferenceCounts', name]);
}

function _setProject(name: string, module: unknown) {
	_set(_getGlobalProjectsObject(), ['projects', name], module);
	_increaseProjectReference(name);
}

function _removeProject(name: string) {
	_decreaseProjectReference(name);
	const referenceCount = _getProjectReferenceCount(name);
	if (!referenceCount) {
		_unset(_getGlobalProjectsObject(), ['projects', name]);
	}
}

function _getProject(name: string) {
	return _get(_getGlobalProjectsObject(), ['projects', name]);
}

function _setProjectInstance(id: string, instance: unknown) {
	_set(_getGlobalProjectsObject(), ['instances', id], instance);
}

function _removeProjectInstance(id: string) {
	_unset(_getGlobalProjectsObject(), ['instances', id]);
}

function _getProjectInstance(id: string) {
	return _get(_getGlobalProjectsObject(), ['instances', id], null);
}

function _getProjects() {
	return _get(_getGlobalProjectsObject(), ['projects']);
}

function _getInstances() {
	return _get(_getGlobalProjectsObject(), ['instances']);
}

function _initGlobalProjectsObject(initialData: IGlobalProjectInterface) {
	if (!_get(window, [ProjectHelper.keyword])) {
		_set(window, [ProjectHelper.keyword], initialData);
	}
}

function _getGlobalProjectsObject(): IGlobalProjectInterface {
	return _get(window, ProjectHelper.keyword);
}

declare global {
	interface Window {
		isAgentsReady: boolean;
		deployAgents: any[];
		agentProjects: AgentProjects;
		getTargetAgentProjectForMobile: (projectName: string, projectVersion?: number) => string;
	}
}

class ProjectHelper {
	public static get isAgentsReady(): boolean {
		return window.isAgentsReady;
	}

	public static get deployAgents(): any[] {
		return window.deployAgents;
	}

	public static get agentProjects(): AgentProjects {
		return window.agentProjects;
	}

	static getTargetAgentProject = (projectName: string, projectVersion?: number) => {
		const agentProject = window.agentProjects?.[projectName];
		if (agentProject) {
			return projectVersion
				? agentProject[projectVersion.toString()]
				: agentProject[Object.keys(agentProject).pop()];
		}
	};

	static setAgentProjects = (projects: AgentProjects) => {
		window.agentProjects = projects;
		Mobile.MobileView.setDeployAgentProjects(projects);
	};
	static setDeployAgents = (deployAgents: any[]) => {
		window.deployAgents = deployAgents;
		Mobile.MobileView.setDeployAgents(deployAgents);
	};
	static setAgentsReady = (isReady: boolean) => {
		window.isAgentsReady = isReady;
	};
	static getDeployAgent = (projectName: string, projectVersion?: number) => {
		if (window.deployAgents.length == 1) {
			return window.deployAgents[0];
		} else if (window.deployAgents.length > 1) {
			const agentProject = window.agentProjects?.[projectName];
			let agent: any = null;
			if (agentProject) {
				const projectWithVersion = projectVersion
					? agentProject[projectVersion.toString()]
					: agentProject[Object.keys(agentProject).pop()];
				agent = window.deployAgents.find(a => a.uId == projectWithVersion?.agentUId);
			} else {
				agent = window.deployAgents.find(a => a.isDefault);
			}
			if (agent) {
				return agent;
			} else {
				return null;
			}
		} else {
			return null;
		}
	};
	static RunGeneratorFlowService = _RunFlowService;
	static RunGeneratorFormService = _RunFormService;
	static RunPromiseFormService = _RunFormServicePromise;
	static RunPromiseFlowService = _RunFlowServicePromise;
	static getDeploymentUrl = _GetDeploymentUrl;

	// Global Project Access

	// eba keywordleri breaking change oluşturmamak adına eklenmiştir. 2024 yılında bu değerler kaldırılabilir.
	static keyword: string = 'SynergyProjects';

	static setProject = _setProject;
	static removeProject = _removeProject;
	static getProject = _getProject;
	static getProjectReferenceCount = _getProjectReferenceCount;
	static increaseProjectReference = _increaseProjectReference;
	static setProjectInstance = _setProjectInstance;
	static removeProjectInstance = _removeProjectInstance;
	static getProjectInstance = _getProjectInstance;
	static getProjects = _getProjects;
	static getInstances = _getInstances;
	static getGlobalProjectsObject = _getGlobalProjectsObject;
	static initGlobalProjectsObject = _initGlobalProjectsObject;
	// Global Project Access : End
}

window.getTargetAgentProjectForMobile = (...args: [string, number?]) => {
	return JSON.stringify(ProjectHelper.getTargetAgentProject(...args));
};

export default ProjectHelper;
export { AgentProjects };
